import { Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import { CompactBand } from '@/components/CompactBand';
import { usd } from '@/components/lists';
import {
  findHistoryClaim,
  recordFromQueue,
  type HistoryClaim,
  type OpenLink,
  type PaymentLine,
} from '../historyMock';
import { usePaidToPatientVersion } from '@/features/networks/paidToPatient';
import { getClaimSync } from '../store';
import { getClaimSync as getArClaim } from '@/features/insurance-ar/store';
import { getClaimSync as getDeniedClaim, getSentRowSync } from '@/features/appeals/store';
import { getItemSync, getPostingSync } from '@/features/posting/store';
import { logHrefForEntry } from '../logLinks';
import { PMS } from '@/features/shared/pms';
import '../claims.css';

/** Resolve a known id: the live queue (when it changed) or the big history fixture. Unknown ids never get here (D202). */
function resolveRecord(id: string): HistoryClaim | null {
  const live = getClaimSync(id);
  if (live && (live.removed || live.held)) return recordFromQueue(live);
  const fromHistory = findHistoryClaim(id);
  if (fromHistory) return fromHistory;
  if (live) return recordFromQueue(live);
  return null;
}

/** "Open now in {page}" opens THIS claim on that page. When the page's own
 *  store has no such id, it falls back to the page root (never /insurance-ar). */
function openHref(open: OpenLink, id: string): string {
  switch (open.page) {
    case 'claims':
      return getClaimSync(id) ? `/claims/${id}` : open.to;
    case 'ar':
      return getArClaim(id) ? `/ar/${id}` : open.to;
    case 'denied':
      return getDeniedClaim(id) || getSentRowSync(id) ? `/denied/${id}` : open.to;
    case 'posting':
      if (getItemSync(id)) return `/posting/${id}`;
      if (getPostingSync(id)) return `/posting/posted/${id}`;
      return open.to;
  }
}

interface DocRow {
  label: string;
  /** A link instead of "View" (the draft is reviewed in Claims to review). */
  link?: { to: string; text: string };
}

/** The "03 Documents" list, derived from the claim's path (rcm-26 §4). */
function documentsOf(rec: HistoryClaim): DocRow[] {
  // Closed as paid to the patient (D197): the payer's EOB is the one document.
  if (rec.paidToPatient) return [{ label: `${rec.payer} EOB, ${rec.paidToPatient.date}` }];
  const has = (label: string) => rec.path.some((s) => s.label === label && !s.none);
  const docs: DocRow[] = [];
  if (has('Sent')) {
    docs.push({ label: 'Claim as sent · ADA form' });
  } else {
    docs.push({
      label: 'Claim draft',
      ...(rec.status === 'waiting' || rec.status === 'rejected'
        ? { link: { to: getClaimSync(rec.id) ? `/claims/${rec.id}` : '/claims', text: 'Review it in Claims to review' } }
        : {}),
    });
  }
  if (has('Rejected')) docs.push({ label: 'Clearinghouse report (rejection)' });
  if (has('Denied')) docs.push({ label: `${rec.payer} ERA (denial)` });
  if (has('Appealed')) docs.push({ label: 'Appeal letter and packet' });
  if (has('Paid')) docs.push({ label: `${rec.payer} ERA (payment)` });
  return docs;
}

function lastNameOf(name: string): string {
  return name.split(' ').slice(-1)[0] ?? name;
}

/** A timeline row's Log link: that same event in ELVA's work, or nothing. */
function LogLink({ patient, iso }: { patient: string; iso: string | undefined }) {
  const href = logHrefForEntry(patient, iso);
  return href ? <Link to={href} className="clm-tl-log">Log</Link> : <span />;
}

/**
 * /claims/record/:id — the permanent, read-only claim record (rcm-26, D149).
 * A claim's whole life on one page: a compact band sentence about where it
 * ended or stands; a header card; the Path row; the "Everything that happened"
 * timeline; and an aside with the money, the documents, and a note that links
 * back to wherever the claim is still open. Any id resolves: the big fixture,
 * the live review queue, or a deterministic synthetic record built from the id.
 */
export function ClaimRecordPage() {
  const { id = '' } = useParams();
  usePaidToPatientVersion();
  usePageTopBar({ breadcrumb: ['Claims', 'Every claim', id] });

  const everyClaim = <Link to="/claims/all" className="cbd-cl">Every claim</Link>;

  const rec = id === '' ? null : resolveRecord(id);
  if (!rec) {
    return (
      <div className="clm clm-record">
        <CompactBand headline="We can’t find that claim." right={everyClaim} />
        <div className="clm-record-body">
          <NotFoundInPage thing="claim" id={id} listLabel="claims" listTo="/claims/all" />
        </div>
      </div>
    );
  }

  const docs = documentsOf(rec);
  const open = rec.openIn;
  const payments: PaymentLine[] =
    rec.payments ?? (rec.paid != null ? [{ amount: rec.paid, date: rec.payerPaidDate ?? '' }] : []);

  const paidKnown = rec.paid != null;
  const answered = rec.allowed != null;
  const noPrice = rec.status === 'replaced' || rec.status === 'not-billed';
  const ptp = rec.paidToPatient;
  const amountLabel = paidKnown && !ptp ? 'Paid' : 'Billed';
  const amountValue = rec.paid != null && !ptp ? usd(rec.paid) : usd(rec.billed);
  const amountSub = ptp
    ? `paid to ${rec.patient.split(' ')[0] ?? rec.patient} · closed ${rec.closedDate ?? ''}`
    : paidKnown
    ? `of ${usd(rec.billed)} billed${rec.closedDate ? ` · closed ${rec.closedDate}` : ''}`
    : rec.where;

  return (
    <div className="clm clm-record">
      <CompactBand
        headline={
          <>
            {rec.band.pre}
            {rec.band.em ? <em>{rec.band.em}</em> : null}
            {rec.band.post ?? null}
          </>
        }
        mint={rec.band.mint}
        right={everyClaim}
      />
      <div className="clm-record-body">
        <div className="clm-rec-cols">
          <main className="clm-rec-main">
            {/* Header card */}
            <section className="clm-card" aria-labelledby="clm-rec-patient">
              <div className="clm-rec-head">
                <div>
                  <div className="clm-eyebrow">
                    Claim <span className="clm-code">{rec.id}</span>
                    {rec.payerClaimNo ? (
                      <> · {rec.payer} claim <span className="clm-code">{rec.payerClaimNo}</span></>
                    ) : null}
                  </div>
                  <h2 id="clm-rec-patient" className="clm-patient">{rec.patient}</h2>
                  <p className="clm-chsub">
                    {rec.plan} · member <span className="clm-member">{rec.memberId}</span> · visit {rec.visitFull} · {rec.provider}
                  </p>
                  {rec.links?.map((l) => (
                    <p key={l.id} className="clm-rec-link">
                      {l.label}: <Link to={`/claims/record/${l.id}`} className="clm-code">{l.id}</Link>
                    </p>
                  ))}
                </div>
                <div className="clm-billed-wrap">
                  <div className="clm-meta">{amountLabel}</div>
                  <div className="clm-billed num">{amountValue}</div>
                  <div className="clm-rec-amtsub">{amountSub}</div>
                </div>
              </div>

              {/* Path */}
              <ol className="clm-path">
                {rec.path.map((s, i) => (
                  <li key={`${s.label}-${String(i)}`} className={`clm-ps${s.bad ? ' bad' : ''}${s.none ? ' none' : ''}${s.outline ? ' outline' : ''}`}>
                    <i aria-hidden />
                    <b>{s.label}{s.count && s.count > 1 ? ` ×${String(s.count)}` : ''}</b>
                    <span className="num">{s.none ? 'No record' : s.date}</span>
                    {s.outline ? <span className="clm-ps-sub">from {PMS}</span> : null}
                  </li>
                ))}
                {rec.nextStep ? (
                  <li className="clm-ps next">
                    <i aria-hidden />
                    <b>{rec.nextStep}</b>
                    {rec.nextNote ? <span className="num">{rec.nextNote}</span> : null}
                  </li>
                ) : null}
              </ol>
              {rec.statusLine ? <p className="clm-rec-statusline">{rec.statusLine}</p> : null}
            </section>

            {/* 01 Everything that happened */}
            <section className="clm-card" aria-labelledby="clm-rec-tl">
              <div className="clm-card-head">
                <span className="clm-sec-no">01</span>
                <h3 className="clm-h3" id="clm-rec-tl">Everything that happened</h3>
                <span className="clm-meta" style={{ marginLeft: 'auto' }}>
                  {rec.timeline.length} {rec.timeline.length === 1 ? 'entry' : 'entries'} · oldest first
                </span>
              </div>
              {rec.firstSeen ? (
                <p className="clm-rec-firstseen">
                  ELVA first saw this claim on {rec.firstSeen}. Earlier steps are from {PMS}.
                </p>
              ) : null}
              <div className="clm-tl">
                {rec.timeline.map((e, i) => (
                  <div key={i} className="clm-tl-row">
                    <span className="clm-tl-stamp num">{e.stamp}</span>
                    <span className={e.ok ? 'clm-tl-tick ok' : 'clm-tl-tick bad'} aria-hidden>
                      {e.ok ? '✓' : '✕'}
                    </span>
                    <span className="clm-tl-body">
                      <b className={e.bad ? 'clm-tl-bad' : undefined}>{e.lead}</b>
                      {e.detail ? <>: {e.detail}</> : null}
                      {e.fromDentrix ? (
                        <span className="clm-tl-dx"> · from {PMS}</span>
                      ) : (
                        <span className="clm-tl-who"> · {e.who}</span>
                      )}
                    </span>
                    {e.fromDentrix ? <span /> : <LogLink patient={rec.patient} iso={e.iso} />}
                  </div>
                ))}
              </div>
            </section>
          </main>

          <aside className="clm-rec-aside">
            {/* 02 The money */}
            <section className="clm-card" aria-labelledby="clm-rec-money">
              <div className="clm-card-head">
                <span className="clm-sec-no">02</span>
                <h3 className="clm-h3" id="clm-rec-money">The money</h3>
              </div>
              {ptp ? (
                <dl className="clm-rec-kv">
                  <dt>Billed</dt><dd className="num">{usd(rec.billed)}</dd>
                  <dt>Allowed</dt><dd>No contract · Dr. {lastNameOf(rec.provider)} is out of network with {rec.payer}</dd>
                  <dt>{rec.payer} paid you</dt><dd className="num">{usd(rec.paid ?? 0)}</dd>
                  <dt>{rec.payer} paid {rec.patient.split(' ')[0] ?? rec.patient}</dt>
                  <dd className="num">{usd(ptp.amount)} · {ptp.date}</dd>
                  <dt>Write-off</dt><dd>None · out of network</dd>
                  <dt>{rec.patient.split(' ')[0] ?? rec.patient} owes</dt>
                  <dd className="num">
                    {usd(rec.billed)} · {usd(ptp.paidAtVisit)} paid at the visit · <b>{usd(rec.patientOwes)} still owed</b>
                  </dd>
                  <dt>Deposit</dt><dd>None · no money came to the practice</dd>
                </dl>
              ) : (
              <dl className="clm-rec-kv">
                <dt>Billed</dt><dd className="num">{usd(rec.billed)}</dd>
                <dt>Allowed</dt>
                {answered ? (
                  <dd className="num">{usd(rec.allowed ?? 0)}</dd>
                ) : !noPrice && rec.expectedAllowed != null ? (
                  <dd className="num clm-rec-expected">Expected {usd(rec.expectedAllowed)} · fee schedule</dd>
                ) : (
                  <dd className="num">—</dd>
                )}
                {payments.length === 0 ? (
                  <><dt>{rec.payer} paid</dt><dd className="num">—</dd></>
                ) : (
                  payments.map((p, i) => (
                    <Fragment key={i}>
                      <dt>{p.amount < 0 ? `${rec.payer} took back` : `${rec.payer} paid`}</dt>
                      <dd className={p.amount < 0 ? 'num clm-rec-minus' : 'num'}>
                        {p.amount < 0 ? `−${usd(-p.amount)}` : usd(p.amount)}{p.date ? ` · ${p.date}` : ''}
                      </dd>
                    </Fragment>
                  ))
                )}
                {payments.length > 1 && rec.paid != null ? (
                  <><dt>Net paid</dt><dd className="num">{usd(rec.paid)}</dd></>
                ) : null}
                <dt>Contractual write-off</dt><dd className="num">{rec.writeOff != null ? usd(rec.writeOff) : '—'}</dd>
                {rec.writtenOff ? (
                  <><dt>Written off</dt><dd className="num">{usd(rec.writtenOff.amount)} · {rec.writtenOff.reason}</dd></>
                ) : null}
                <dt>Patient owes</dt><dd className="num">{answered ? usd(rec.patientOwes) : '—'}</dd>
                <dt>Deposit</dt>
                <dd className="clm-rec-nowrap">
                  {rec.depositDate ? (
                    <>{rec.depositDate} · <Link to="/posting/bank">Bank deposits</Link></>
                  ) : rec.path.some((s) => s.label === 'Deposited' && s.none) ? (
                    rec.noDepositReason ?? 'No record yet'
                  ) : (
                    '—'
                  )}
                </dd>
              </dl>
              )}
              {rec.short != null && rec.expectedPay != null && rec.paid != null ? (
                <p className="clm-rec-short num">
                  Expected {usd(rec.expectedPay)} · paid {usd(rec.paid)} · short {usd(rec.short)}
                </p>
              ) : null}
              {rec.owesNote ? <p className="clm-rec-owesnote">{rec.owesNote}</p> : null}
            </section>

            {/* 03 Documents */}
            <section className="clm-card" aria-labelledby="clm-rec-docs">
              <div className="clm-card-head">
                <span className="clm-sec-no">03</span>
                <h3 className="clm-h3" id="clm-rec-docs">Documents</h3>
              </div>
              {docs.map((d) => (
                <div key={d.label} className="clm-rec-doc">
                  <span>{d.label}</span>
                  {d.link ? (
                    <Link to={d.link.to} className="clm-tl-log">{d.link.text}</Link>
                  ) : (
                    <button type="button" className="clm-tl-log">View</button>
                  )}
                </div>
              ))}
            </section>

            <section className="clm-card clm-rec-note">
              {open ? (
                <>Open now in <Link to={openHref(open, rec.id)}>{open.label}</Link>.</>
              ) : rec.asideNote ? (
                <>{rec.asideNote}</>
              ) : (
                <>Read-only: this claim is closed. Anything still open shows on its own page (Claims, Insurance A/R, Denied &amp; underpaid, Posting) with a link back here.</>
              )}
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
